import React, { useEffect } from 'react';
import { CheckCircle, XCircle, Info, AlertCircle, X } from 'lucide-react';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

interface ToastProps {
  id: string;
  type: ToastType;
  title: string;
  message?: string;
  duration?: number;
  onClose: (id: string) => void;
}

const iconMap = {
  success: CheckCircle,
  error: XCircle,
  info: Info,
  warning: AlertCircle,
};

const colorMap = {
  success: 'text-green-600 bg-green-50 border-green-200',
  error: 'text-red-600 bg-red-50 border-red-200',
  info: 'text-blue-600 bg-blue-50 border-blue-200',
  warning: 'text-yellow-600 bg-yellow-50 border-yellow-200',
};

export const Toast: React.FC<ToastProps> = ({
  id,
  type,
  title,
  message,
  duration = 5000,
  onClose,
}) => {
  // Ensure type is valid, fallback to 'info' if not
  const safeType = (type && colorMap[type]) ? type : 'info';
  const Icon = iconMap[safeType];
  const colors = colorMap[safeType]?.split(' ') || ['text-blue-600', 'bg-blue-50', 'border-blue-200'];

  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(() => {
        onClose(id);
      }, duration);

      return () => clearTimeout(timer);
    }
    // Return empty cleanup function when duration is 0 or negative
    return () => {};
  }, [id, duration, onClose]);

  return (
    <div
      className={`
        glass-card backdrop-blur-md bg-white/95 
        ${colors[2]} border-l-4
        p-4 rounded-lg shadow-lg 
        transform transition-all duration-300 ease-in-out
        hover:scale-[1.02] hover:shadow-xl
        animate-slide-in-right
      `}
    >
      <div className="flex items-start">
        <div className={`flex-shrink-0 ${colors[0]}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="ml-3 flex-1">
          <p className={`text-sm font-medium ${colors[0]}`}>
            {title}
          </p>
          {message && (
            <p className="mt-1 text-sm text-neutral-600">{message}</p>
          )}
        </div>
        <button
          onClick={() => onClose(id)}
          className="ml-4 flex-shrink-0 inline-flex text-neutral-400 hover:text-neutral-600 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};